import { Field } from "@sitecore-jss/sitecore-jss-react";
import React, { ChangeEvent } from "react";
import { v4 } from "uuid";

import { ModelState } from "../../interfaces/modelState";
import { ValidationMessage } from "../validationMessage/ValidationMessage";
import sanitizeInput, { Sanitizers } from "./sanitizeInput";
import useFieldName from "./useFieldName";
import { Validators } from "./validator/validators";

interface Props {
  fieldData: Record<string, Field<string>>;
  name: string;
  fieldName?: string;
  modelState?: ModelState | null;
  setModelState?: (modelState: ModelState) => void;
  inputValue?: number;
  defaultInputValue?: number;
  id?: string;
  sanitize?: Sanitizers<number>;
  validators?: Validators<number>;
  onChangeCommitted?: () => void;
  onChange?: (value: number) => void;
  propertyName?: string;
  excludeRow?: boolean;
  widthClassName?: string;
  capitalize?: boolean;
  required?: boolean;
  disabled?: boolean;
  min: number;
  max: number;
  step?: number;
  dynamicMinLabel?: string;
  dynamicMaxLabel?: string;
}

const SliderInput = (props: Props) => {
  const { name, fieldName } = useFieldName(props.name, props.fieldName);
  const leftLabelFieldName = "MinLabel" + name;
  const rightLabelFieldName = "MaxLabel" + name;

  const leftLabel = getDynamicLabel(props.fieldData, leftLabelFieldName, props.dynamicMinLabel);
  const rightLabel = getDynamicLabel(props.fieldData, rightLabelFieldName, props.dynamicMaxLabel);
  const inputValue = props.inputValue || props.defaultInputValue || 0;

  const sliderStyleValue = (inputValue - props.min) / (props.max - props.min);
  const background = "#FFF";
  const inputId = `${props.id || fieldName}${v4()}`;
  const styleName = `rangeStyle${inputId}`;

  const handleNumberInput = (e: ChangeEvent<HTMLInputElement>) => {
    const number = sanitizeInput(parseFloat(e.target.value), props.sanitize);
    if (props.onChange) {
      props.onChange(number);
    }
  };

  const handleRelease = () => {
    if (props.onChangeCommitted && props.inputValue) {
      props.onChangeCommitted();
    }
  };

  const isFirefox = "InstallTrigger" in window;
  const isChrome = "chrome" in window;

  return (
    <>
      {isChrome && (
        <style id={styleName}>
          {`input[type='range'][id='${inputId}']::-webkit-slider-runnable-track { background-image: -webkit-gradient(linear, left top, right top, color-stop(${sliderStyleValue}, #66B3C9), color-stop(${sliderStyleValue},${background})) !important; }`}
        </style>
      )}
      {isFirefox && (
        <style id={styleName}>
          {`input[type='range'][id='${inputId}']::-moz-range-track  { background-image:  linear-gradient(to right, #66B3C9 ${
            sliderStyleValue * 100
          }%, ${background} 0%) !important; }`}
        </style>
      )}
      <div className="form-row">
        <div className="form-item range">
          <div className="form-input range">
            <input
              disabled={props.disabled}
              type="range"
              id={inputId}
              name={fieldName}
              min={props.min}
              max={props.max}
              step={props.step}
              value={inputValue}
              onMouseUp={handleRelease}
              onTouchEnd={handleRelease}
              onKeyUp={handleRelease}
              onChange={handleNumberInput}
            />
          </div>
          <div className="labels">
            {leftLabel && <div className="left">{leftLabel}</div>}
            {rightLabel && <div className="right">{rightLabel}</div>}
          </div>
        </div>
        <ValidationMessage modelState={props.modelState} property={props.propertyName} />
      </div>
    </>
  );
};

export default SliderInput;

function getDynamicLabel(
  fieldData: Props["fieldData"],
  fieldName: Props["fieldName"],
  dynamicLabel: Props["dynamicMaxLabel"] | Props["dynamicMinLabel"]
) {
  if (dynamicLabel) {
    return dynamicLabel;
  }

  return fieldName && fieldData[fieldName] ? fieldData[fieldName].value : "";
}
